import { Chip, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import colors from 'common/constants/colors';
import PropTypes from 'prop-types';

/**
 * POM-6010 — the uncommitted-changes badge and the Commit button, as one unit.
 *
 * They belong together and they belong next to the grid: the badge counts edits made in the rows
 * below, and Commit is what clears it. In the header they sat a full banner away from the thing
 * they described, so a change applied at the bottom of a long grid reported itself off-screen.
 *
 * Rendered at the end of the filter row, which only exists when there are rows to review — and
 * since edits are only possible on those rows, a batch with nothing listed can have nothing to
 * commit. That is why it is safe for this to disappear with the filters rather than sitting
 * permanently disabled.
 */
const countOf = (count, noun) => `${count} ${noun}${count > 1 ? 's' : ''}`;

/**
 * POM-6536 — `changedCount` includes the staged claims, `claimCount` says how many of them are
 * claims. A batch with no claims reads exactly as before.
 */
const BulkLotNdcCommitAction = ({
  changedCount,
  claimCount,
  committing,
  onCommit,
  leadingAction,
}) => {
  const lotChangeCount = changedCount - claimCount;
  const parts = [
    lotChangeCount > 0 ? countOf(lotChangeCount, 'Change') : '',
    claimCount > 0 ? countOf(claimCount, 'Claim') : '',
  ].filter(Boolean);
  const pendingLabel =
    claimCount > 0
      ? `${parts.join(' & ').toLowerCase()} uncommitted`
      : `${changedCount} uncommitted change${changedCount > 1 ? 's' : ''}`;

  return (
    /*
     * mr clears the grid's tool-panel strip. The Columns/Filters rail runs down the right edge of
     * the grid below, so a control pushed hard to the row's end sits over that strip's width and
     * reads as jammed against the window rather than aligned with anything.
     */
    <Stack direction='row' spacing={1} alignItems='center' sx={{ ml: 'auto', mr: 5 }}>
      {leadingAction}
      {changedCount > 0 && (
        <Chip
          // Explicit amber, not color='warning': in perfectRxTheme palette.warning.main is the
          // brand navy with a near-black contrastText, so a warning-coloured Chip renders
          // dark-on-dark and cannot be read. The real amber is what colors.warningAmber holds.
          sx={{
            bgcolor: colors.warningAmber,
            color: colors.warningAmberText,
            fontWeight: 600,
          }}
          label={pendingLabel}
        />
      )}
      <LoadingButton
        variant='contained'
        loading={committing}
        disabled={changedCount === 0}
        onClick={onCommit}
      >
        {changedCount > 0 ? `Commit ${parts.join(' & ')}` : 'Commit Changes'}
      </LoadingButton>
    </Stack>
  );
};

BulkLotNdcCommitAction.propTypes = {
  changedCount: PropTypes.number.isRequired,
  claimCount: PropTypes.number,
  committing: PropTypes.bool.isRequired,
  onCommit: PropTypes.func.isRequired,
  /** A control that sits before the pending count and Commit (POM-6537: Bulk lot update). */
  leadingAction: PropTypes.node,
};

BulkLotNdcCommitAction.defaultProps = {
  claimCount: 0,
  leadingAction: null,
};

export default BulkLotNdcCommitAction;
